import type { PackEntry } from 'types/shared';
import type { AdSetEntry } from '../api/getAdSets';
import { PartnershipCreationMode } from '../types';

export interface CalculateAdsCountParams {
  packsWithCreatives: PackEntry[];
  selectedAdSets: AdSetEntry[];
  duplicateCounts: Record<string, string | number>;
  isMarketingProcessInbox: boolean;
  adsPerCreative: number;
}

const countPackCreatives = (pack: PackEntry): number =>
  pack.creatives.reduce(
    (count, id) => count + (typeof id === 'string' && id.length > 0 ? 1 : 0),
    0,
  );

const getDuplicatesCount = (
  adSetId: string,
  duplicateCounts: Record<string, string | number>,
  isMarketingProcessInbox: boolean,
): number => {
  if (isMarketingProcessInbox) {
    return 1;
  }

  const count = Number(duplicateCounts[adSetId]);
  return Number.isFinite(count) && count >= 1 ? count : 1;
};

const getDuplicateSum = (
  selectedAdSets: AdSetEntry[],
  duplicateCounts: Record<string, string | number>,
  isMarketingProcessInbox: boolean,
): number =>
  selectedAdSets.reduce(
    (sum, { id }) => sum + getDuplicatesCount(id, duplicateCounts, isMarketingProcessInbox),
    0,
  );

export const getPartnershipCount = (
  partnershipAdsEnabled: boolean,
  partnershipAdSettings: { partnershipId?: string }[] | undefined,
): number => {
  if (!partnershipAdsEnabled) {
    return 0;
  }

  return (
    partnershipAdSettings?.filter(
      (setting) => Boolean(setting.partnershipId?.trim()),
    ).length ?? 0
  );
};

export const getAdsPerCreativeMultiplier = (
  partnershipAdsEnabled: boolean,
  partnershipAdSettings: { partnershipId?: string }[] | undefined,
  partnershipCreationMode: PartnershipCreationMode | undefined,
): number => {
  const partnershipCount = getPartnershipCount(
    partnershipAdsEnabled,
    partnershipAdSettings,
  );

  if (partnershipCount < 1) {
    return 1;
  }

  // One partnership ad per partner (creative is reused per partner);
  // BOTH mode creates 2 ads per partner (App identity + App+PA identity).
  const adsPerPartner =
    partnershipCreationMode === PartnershipCreationMode.BOTH ? 2 : 1;

  return partnershipCount * adsPerPartner;
};

export const getPartnershipBudgetMultiplier = (
  partnershipAdsEnabled: boolean,
  partnershipAdSettings: { partnershipId?: string }[] | undefined,
): number => {
  const partnershipCount = getPartnershipCount(
    partnershipAdsEnabled,
    partnershipAdSettings,
  );

  // Budget = (Campaigns Budget) × nPA; creation mode never affects it.
  return partnershipCount < 1 ? 1 : partnershipCount;
};

export const calculateScalingEgAdsCount = ({
  selectedCreatives,
  selectedAdSets,
  adsPerCreative,
}: {
  selectedCreatives: string[];
  selectedAdSets: AdSetEntry[];
  adsPerCreative: number;
}): number => {
  if (!selectedAdSets.length || !selectedCreatives.length) {
    return 0;
  }

  return selectedCreatives.length * selectedAdSets.length * adsPerCreative;
};

export const calculateAdsCount = ({
  packsWithCreatives,
  selectedAdSets,
  duplicateCounts,
  isMarketingProcessInbox,
  adsPerCreative,
}: CalculateAdsCountParams): number => {
  if (!selectedAdSets.length || !packsWithCreatives.length) {
    return 0;
  }

  const adsPerPackBase =
    getDuplicateSum(selectedAdSets, duplicateCounts, isMarketingProcessInbox) *
    adsPerCreative;

  return packsWithCreatives.reduce(
    (total, pack) => total + countPackCreatives(pack) * adsPerPackBase,
    0,
  );
};
